import { isToday } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { EventDoc } from 'types/api/changes'
import { isConvertedGroupId } from 'utils/groups'

export function isSomeDayInstance(event: EventDoc) {
  if (!event) return false

  return (
    event.StartDate === 'null' ||
    event.StartDate === '' ||
    event.someday === '1'
  )
}

export function isLateInstance(event: EventDoc, ignoreRecurrence?: boolean) {
  if (!event || !event.StartDate || event.someday === '1') return false

  if (
    ignoreRecurrence &&
    event.Recurrence &&
    typeof event.Recurrence !== 'string'
  ) {
    return false
  }

  const now = dayjs()

  return (
    event.EndDate !== undefined &&
    dayjs(Number(event.EndDate) * 1000).isBefore(now.startOf('day'))
  )
}

export function isEventRecurrenceInstanceValid(
  event: EventDoc,
  visibleAreaStart: dayjs.Dayjs
) {
  const isEventRecurrenceValid =
    event.Recurrence != null &&
    event.Recurrence != undefined &&
    typeof event.Recurrence !== 'string' &&
    (!event.Recurrence.EndDate ||
      Number(event.Recurrence.EndDate) >= visibleAreaStart.unix())

  return isEventRecurrenceValid
}

export function isEventInstanceWithinTimePeriod(
  event: EventDoc,
  visibleAreaStart: Dayjs,
  visibleAreaEnd: Dayjs
): boolean {
  let itemStartDate = dayjs(Number(event.StartDate) * 1000)
  const itemEndDate = dayjs(Number(event.EndDate) * 1000).subtract(
    isConvertedGroupId(event.GroupID) && event.AllDay === '1' ? 1 : 0,
    'day'
  )

  /**
   * Because of a bug on iOS while adding all day events, they are added in devices timezone midnight as oppose to utc midnight, in order to fix that, we
   * need to add extra 13 hours to cover all timezones, and then make it start of the beginning of a day, we also can check the timezone which
   * will be empty for such events
   */

  if (
    event.AllDay === '1' &&
    (event.TimeZoneNameID?.length === 0 || event.TimeZoneNameID !== 'UTC')
  ) {
    itemStartDate = itemStartDate.add(13, 'hour')
  }

  if (isToday(visibleAreaStart.toDate()) && event.lateOriginalStartTime) {
    return true // all late events should be shown under today
  }

  const visibleStartCheck =
    itemStartDate.isSameOrAfter(visibleAreaStart.startOf('day')) &&
    itemStartDate.isSameOrBefore(visibleAreaEnd.endOf('day'))
  const visibleEndCheck =
    itemStartDate.isSameOrBefore(visibleAreaStart.startOf('day')) &&
    itemEndDate.isSameOrAfter(visibleAreaStart.startOf('day')) &&
    itemEndDate.isSameOrBefore(visibleAreaEnd.endOf('day'))
  const startBeforeEndAfterCheck =
    itemStartDate.isBefore(visibleAreaStart.startOf('day')) &&
    itemEndDate.isAfter(visibleAreaEnd.endOf('day'))

  return (
    visibleStartCheck || // event starts within visible area
    visibleEndCheck || // event ends within visible area
    startBeforeEndAfterCheck // event starts before and ends after visible area
  )
}

export function eventLengthInDays(event: EventDoc) {
  if (!event.StartDate || !event.EndDate) return 0

  // Convert Unix timestamps to milliseconds
  const startTimestamp = Number(event.StartDate) * 1000
  const endTimestamp = Number(event.EndDate) * 1000

  // Create normalized dates (set to midnight)
  const startDateDay = new Date(startTimestamp)
  startDateDay.setHours(0, 0, 0, 0)

  const endDateDay = new Date(endTimestamp)
  endDateDay.setHours(0, 0, 0, 0)

  // Calculate difference in days
  return Math.round(
    (endDateDay.getTime() - startDateDay.getTime()) / (24 * 60 * 60 * 1000)
  )
}
